import { useEffect, useMemo, useRef, useState, type SetStateAction } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider.js';
import { toUserMessage } from '../errors.js';
import { getSyncEngine } from '../sync/engine.js';
import {
  deleteRegisterDraft, listOfflineRegisterRows, readRegisterDraft, saveOfflineRegister, writeRegisterDraft,
  type OfflineRegisterTable, type RegisterScope, type RegisterSyncState,
} from './offlineRegisters.js';

export function offlineRegisterStatus(row: RegisterSyncState): string | null {
  if (row._syncStatus === 'pending') return 'Salvato sul dispositivo · In attesa di sincronizzazione';
  if (row._syncStatus === 'rejected') {
    return 'Sincronizzazione rifiutata. ' + (row._syncError ? toUserMessage(new Error(row._syncError)) : 'Controlla i dettagli in Diagnostica.');
  }
  return null;
}

interface Editor<Form> {
  key: string;
  form: Form;
  id: string | null;
  ready: boolean;
  open: boolean;
  saving: boolean;
  draftSaving: boolean;
  draftError: string | null;
}

/** One durable draft per account, restaurant, business and register. */
export function useOfflineRegister<Row extends { id: string }, Form extends Record<string, unknown>>(
  table: OfflineRegisterTable,
  emptyForm: Form,
  dateKey: keyof Row,
  canEdit: boolean,
) {
  const { user, profile } = useAuth();
  const client = useQueryClient();
  const accountId = user?.id;
  const tenantId = profile?.active_tenant_id;
  const businessId = profile?.active_business_id ?? null;
  const scope = useMemo<RegisterScope | null>(() => accountId && tenantId
    ? { accountId, tenantId, businessId } : null, [accountId, tenantId, businessId]);
  const key = JSON.stringify([table, accountId, tenantId, businessId]);
  const queryKey = ['masters', table, accountId, tenantId, businessId];
  const active = useRef({ key, canEdit, scope });
  active.current = { key, canEdit, scope };
  const mounted = useRef(true);
  const blank = (): Editor<Form> => ({ key, form: { ...emptyForm }, id: null, ready: false,
    open: false, saving: false, draftSaving: false, draftError: null });
  const [editor, setEditor] = useState<Editor<Form>>(blank);
  const current = useRef(editor);
  const writes = useRef(Promise.resolve());
  const latestDrafts = useRef(new Map<string, Editor<Form>>());
  const saves = useRef(new Map<string, Promise<void>>());
  const writeVersion = useRef(0);
  const [readVersion, retryRead] = useState(0);

  function publish(next: Editor<Form>) {
    if (!mounted.current || active.current.key !== next.key || active.current.scope !== scope) return;
    current.current = next;
    setEditor(next);
  }

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const next = blank();
    publish(next);
    if (!scope) return;
    void Promise.resolve().then(async () => {
      // A returning account must not edit a draft an earlier save/discard is still consuming.
      await saves.current.get(key)?.catch(() => {});
      await writes.current;
      return readRegisterDraft<Form>(scope, table);
    }).then((draft) => {
      if (cancelled) return;
      publish({ ...next, ready: true, id: draft?.id ?? null,
        form: draft ? { ...emptyForm, ...draft.payload } : { ...emptyForm },
        open: !!draft && active.current.canEdit });
    }).catch((error: unknown) => {
      if (!cancelled) publish({ ...next, draftError: 'Impossibile recuperare la bozza: ' + toUserMessage(error) });
    });
    return () => { cancelled = true; };
    // The form defaults are module constants; a scope change must retire every old response.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, scope, table, emptyForm, readVersion]);

  const register = useQuery<Array<Row & RegisterSyncState>>({
    queryKey,
    enabled: !!scope,
    networkMode: 'always',
    queryFn: async () => {
      if (!scope) return [];
      const rows = await listOfflineRegisterRows<Row>(scope, table);
      return rows.sort((a, b) => String(b[dateKey] ?? '').localeCompare(String(a[dateKey] ?? '')));
    },
  });

  function persistDraft(next: Editor<Form>) {
    if (!scope || !next.id || active.current.scope !== scope || !active.current.canEdit) return;
    const id = next.id;
    const writeKey = JSON.stringify([next.key, id]);
    latestDrafts.current.set(writeKey, next);
    const version = ++writeVersion.current;
    publish({ ...next, draftSaving: true, draftError: null });
    writes.current = writes.current.then(async () => {
      // Skip obsolete queued keystrokes, but always flush the newest form before confirming it.
      if (latestDrafts.current.get(writeKey) !== next) return;
      try {
        await writeRegisterDraft(scope, table, { id, payload: next.form });
      } finally {
        if (latestDrafts.current.get(writeKey) === next) latestDrafts.current.delete(writeKey);
      }
    })
      .then(() => {
        if (version === writeVersion.current && current.current.key === next.key) {
          publish({ ...current.current, draftSaving: false, draftError: null });
        }
      }).catch((error: unknown) => {
        if (version === writeVersion.current && current.current.key === next.key) {
          publish({ ...current.current, draftSaving: false, draftError: 'Bozza non salvata sul dispositivo: ' + toUserMessage(error) });
        }
      });
  }

  function start() {
    const state = current.current;
    if (!scope || active.current.key !== state.key || !active.current.canEdit || !state.ready || state.saving) return;
    const next = { ...state, open: true, id: state.id ?? crypto.randomUUID() };
    persistDraft(next);
  }

  function setForm(update: SetStateAction<Form>) {
    const state = current.current;
    if (active.current.key !== state.key || !active.current.canEdit || !state.open || state.saving) return;
    const form = typeof update === 'function' ? update(state.form) : update;
    persistDraft({ ...state, form });
  }

  function close() {
    const state = current.current;
    if (active.current.key === state.key && !state.saving) publish({ ...state, open: false });
  }

  async function save(patch: Record<string, unknown>): Promise<boolean> {
    const state = current.current;
    if (state.saving) return false;
    if (!scope || active.current.key !== state.key || !active.current.canEdit || !state.ready || !state.id) {
      throw new Error('Sessione o autorizzazione non disponibile. Riapri il registro.');
    }
    publish({ ...state, saving: true });
    try {
      await writes.current;
      if (active.current.scope !== scope || !active.current.canEdit) return false;
      const pending = saveOfflineRegister({ scope, table, id: state.id, patch });
      saves.current.set(key, pending);
      try {
        await pending;
      } finally {
        if (saves.current.get(key) === pending) saves.current.delete(key);
      }
      void client.invalidateQueries({ queryKey: ['masters', table] });
      if (active.current.scope !== scope) return false;
      publish({ ...blank(), ready: true });
      if (navigator.onLine) void getSyncEngine().tick().catch(() => undefined);
      return true;
    } catch (error) {
      if (active.current.scope !== scope) return false;
      throw error;
    } finally {
      if (active.current.scope === scope) publish({ ...current.current, saving: false });
    }
  }

  async function discard(): Promise<boolean> {
    const state = current.current;
    if (state.saving) return false;
    if (!scope || active.current.scope !== scope || active.current.key !== state.key
      || !active.current.canEdit || !state.ready || !state.id) return false;
    publish({ ...state, saving: true });
    try {
      await writes.current;
      await saves.current.get(key)?.catch(() => {});
      if (active.current.scope !== scope || !active.current.canEdit) return false;
      const pending = deleteRegisterDraft(scope, table);
      saves.current.set(key, pending);
      try {
        await pending;
      } finally {
        if (saves.current.get(key) === pending) saves.current.delete(key);
      }
      if (active.current.scope !== scope) return false;
      publish({ ...blank(), ready: true });
      return true;
    } catch (error) {
      if (active.current.scope !== scope) return false;
      publish({ ...current.current, draftError: 'Bozza non eliminata: ' + toUserMessage(error) });
      throw error;
    } finally {
      if (active.current.scope === scope) publish({ ...current.current, saving: false });
    }
  }

  const visible = editor.key === key ? editor : blank();
  return {
    register, form: visible.form, setForm, open: visible.open && canEdit,
    start, close, save, discard, saving: visible.saving, ready: visible.ready && !!scope,
    hasDraft: !!visible.id, draftError: visible.draftError,
    hasDraftChanges: Object.keys(emptyForm).some((field) => !Object.is(visible.form[field], emptyForm[field])),
    draftStatus: visible.draftSaving ? 'Salvataggio della bozza…' : visible.id && !visible.draftError ? 'Bozza salvata sul dispositivo.' : null,
    retryDraft: () => visible.ready ? persistDraft(current.current) : retryRead((version) => version + 1),
  };
}
